import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ModalContext } from "../../../store/ModalContext";
import {
  StepNavPanel,
  type StepNavPanelHandle,
} from "../../../modals/StepNavPanel";
import "../../../modals/Modal.css";

const HARVEST_LOG_STEP = 0;
const ORDERS_STEP = 1;
const PICKUP_STEP = 2;
const SUB_TIER_STEP = 3;
const PRICE_STEP = 4;
const CONFIRM_STEP = 5;

type StartSeasonModalBodyProps = {
  onConfirm: () => Promise<void> | void;
};

export function StartSeasonModalBody({ onConfirm }: StartSeasonModalBodyProps) {
  const { closeModal } = useContext(ModalContext);
  const stepNavRef = useRef<StepNavPanelHandle>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onConfirm();
      closeModal();
    } finally {
      setIsSubmitting(false);
    }
  }, [onConfirm, closeModal, isSubmitting]);

  const steps = useMemo(
    () => [
      {
        content: (
          <div className="operation-controls-modal-step">
            <p className="modal-exit-confirm-msg">
              Have you printed and deleted your harvest logs?
            </p>
            <p>
              It's a good idea to start fresh each year. Print your logs for
              your records, then delete them before the new season to start
              fresh.
            </p>
            <p className="operation-controls-modal-hint">
              <Link to="/dashboard/admin/harvest-log" onClick={closeModal}>
                Go to Harvest Log
              </Link>
            </p>
          </div>
        ),
        footer: (
          <div className="buttons-footer">
            <button
              type="button"
              className="btn equal-btns"
              onClick={closeModal}
            >
              No, Go Back
            </button>
            <button
              type="button"
              className="btn equal-btns"
              onClick={() => stepNavRef.current?.goToStep(ORDERS_STEP)}
            >
              Yes, Next!
            </button>
          </div>
        ),
      },
      {
        content: (
          <div className="operation-controls-modal-step">
            <p className="modal-exit-confirm-msg">
              Have you printed your orders?
            </p>
            <p>
              You can use the year filter on the orders page to view orders from
              past years. But it could be a good idea to keep a printed record
              of your orders.
            </p>
            <p className="operation-controls-modal-hint">
              <Link to="/dashboard/admin/orders" onClick={closeModal}>
                Go to Orders
              </Link>
            </p>
          </div>
        ),
        footer: (
          <div className="buttons-footer">
            <button
              type="button"
              className="btn equal-btns"
              onClick={() => stepNavRef.current?.goToStep(HARVEST_LOG_STEP)}
            >
              No, Go Back
            </button>
            <button
              type="button"
              className="btn equal-btns"
              onClick={() => stepNavRef.current?.goToStep(PICKUP_STEP)}
            >
              Yes, Next!
            </button>
          </div>
        ),
      },
      {
        content: (
          <div className="operation-controls-modal-step">
            <p className="modal-exit-confirm-msg">
              Have you updated the <strong>Pickup Locations</strong>? These
              cannot be changed during the season.
            </p>
            <p className="operation-controls-modal-hint">
              <Link to="/dashboard/admin/pickup-locations" onClick={closeModal}>
                Go to Pickup Locations
              </Link>
            </p>
          </div>
        ),
        footer: (
          <div className="buttons-footer">
            <button
              type="button"
              className="btn equal-btns"
              onClick={() => stepNavRef.current?.goToStep(ORDERS_STEP)}
            >
              No, Go Back
            </button>
            <button
              type="button"
              className="btn equal-btns"
              onClick={() => stepNavRef.current?.goToStep(SUB_TIER_STEP)}
            >
              Yes, Next!
            </button>
          </div>
        ),
      },
      {
        content: (
          <div className="operation-controls-modal-step">
            <p className="modal-exit-confirm-msg">
              Have you updated the <strong>Subscriptions and Tiers</strong>?
              These cannot be changed during the season.
            </p>
            <p className="operation-controls-modal-hint">
              <Link
                to="/dashboard/admin/csa-subscriptions"
                onClick={closeModal}
              >
                Go to Subscriptions
              </Link>
            </p>
          </div>
        ),
        footer: (
          <div className="buttons-footer">
            <button
              type="button"
              className="btn equal-btns"
              onClick={() => stepNavRef.current?.goToStep(PICKUP_STEP)}
            >
              No, Go Back
            </button>
            <button
              type="button"
              className="btn equal-btns"
              onClick={() => stepNavRef.current?.goToStep(PRICE_STEP)}
            >
              Yes, Next!
            </button>
          </div>
        ),
      },
      {
        content: (
          <div className="operation-controls-modal-step">
            <p className="modal-exit-confirm-msg">
              Did you edit the <strong>CSA Delivery Price</strong>?
            </p>
            <p className="operation-controls-modal-hint">
              <Link
                to="/dashboard/admin/operation-controls"
                onClick={closeModal}
              >
                Go to Operation Controls
              </Link>
            </p>
          </div>
        ),
        footer: (
          <div className="buttons-footer">
            <button
              type="button"
              className="btn equal-btns"
              onClick={() => stepNavRef.current?.goToStep(PRICE_STEP)}
            >
              No, Go Back
            </button>
            <button
              type="button"
              className="btn equal-btns"
              onClick={() => stepNavRef.current?.goToStep(CONFIRM_STEP)}
            >
              Yes, Next!
            </button>
          </div>
        ),
      },
      {
        content: (
          <div className="operation-controls-modal-step">
            <div className="info-card warning-card">
              Note for ending the season later: <br />
              <strong>I understand</strong> that I should{" "}
              <strong>never end</strong> the season until <strong>after</strong>{" "}
              the last subscription is delivered.
              <p>
                When you end the season, all CSA accounts get downgraded to
                social accounts. This means you lose the data if you end the
                season.
              </p>
            </div>
            <br />

            <p className="modal-exit-confirm-msg">
              Do you want to start the new season?
            </p>
          </div>
        ),
        footer: (
          <div className="buttons-footer">
            <button
              type="button"
              className="btn equal-btns"
              onClick={() => stepNavRef.current?.goToStep(SUB_TIER_STEP)}
            >
              No, Go Back
            </button>
            <button
              type="button"
              className="btn equal-btns"
              disabled={isSubmitting}
              onClick={() => void handleConfirm()}
            >
              {isSubmitting ? "Starting..." : "Yes, Start Season!"}
            </button>
          </div>
        ),
      },
    ],
    [closeModal, handleConfirm, isSubmitting],
  );

  return (
    <StepNavPanel ref={stepNavRef} steps={steps} className="modal-step-nav" />
  );
}
